import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../components/LoginGate';
import { canSeeFinance } from '../../lib/constants';

// `total_value` and `total_collected` are aggregate columns stripped
// for the staff role by `stripFinancialForStaff` — optional in the type
// so TS matches reality.
interface Client {
  id: number;
  name: string;
  client_id: string | null;
  contact: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  follow_up_tasks: string | null;
  case_count: number;
  total_value?: number;
  total_collected?: number;
}

interface ClientForm {
  name: string;
  client_id: string;
  contact: string;
  email: string;
  address: string;
  notes: string;
  follow_up_tasks: string;
}

const emptyForm: ClientForm = {
  name: '', client_id: '', contact: '', email: '', address: '', notes: '', follow_up_tasks: '',
};

type DeleteMode = 'archive' | 'full' | null;

// Defensive — `total_value` / `total_collected` aren't currently on the
// server's finance-strip list (see Backend/middlewares/rbac.ts), but if
// that list ever grows we don't want an `undefined.toLocaleString()` to
// white-screen the Clients page.
function formatCurrency(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ClientsPage() {
  const { user } = useAuth();
  const showFinance = canSeeFinance(user?.role);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [form, setForm] = useState<ClientForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [deleteMode, setDeleteMode] = useState<DeleteMode>(null);
  const [deleting, setDeleting] = useState(false);

  function fetchClients() {
    apiFetch('/api/clients')
      .then(r => r.json())
      .then((data: Client[]) => { setClients(data); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { fetchClients(); }, []);

  function openNewModal() { setEditingClient(null); setForm(emptyForm); setShowModal(true); }
  function openEditModal(client: Client) {
    setEditingClient(client);
    setForm({
      name: client.name,
      client_id: client.client_id || '',
      contact: client.contact || '',
      email: client.email || '',
      address: client.address || '',
      notes: client.notes || '',
      follow_up_tasks: client.follow_up_tasks || '',
    });
    setShowModal(true);
  }
  function closeModal() { setShowModal(false); setEditingClient(null); setForm(emptyForm); }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const url = editingClient ? `/api/clients/${editingClient.id}` : '/api/clients';
      const method = editingClient ? 'PUT' : 'POST';
      await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          client_id: form.client_id.trim() || null,
          contact: form.contact.trim() || null,
          email: form.email.trim() || null,
          address: form.address.trim() || null,
          notes: form.notes.trim() || null,
          follow_up_tasks: form.follow_up_tasks.trim() || null,
        }),
      });
      closeModal();
      fetchClients();
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deletingClient || !deleteMode) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/clients/${deletingClient.id}?mode=${deleteMode}`, { method: 'DELETE' });
      setDeletingClient(null);
      setDeleteMode(null);
      fetchClients();
    } finally { setDeleting(false); }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">Manage your client database</p>
        </div>
        <button className="btn btn-primary" onClick={openNewModal}>New Client</button>
      </div>

      <div className="card">
        <div className="table-container">
          {clients.length === 0 ? (
            <div className="empty-state"><p>No clients found. Click "New Client" to add one.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>File Reference</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Cases</th>
                  {showFinance && <th>Total Value (£)</th>}
                  {showFinance && <th>Collected (£)</th>}
                  {showFinance && <th>Outstanding</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => {
                  const outstanding = (client.total_value ?? 0) - (client.total_collected ?? 0);
                  return (
                    <tr key={client.id}>
                      <td style={{ fontWeight: 600 }}>{client.name}</td>
                      <td>{client.client_id || '-'}</td>
                      <td>{client.contact || '-'}</td>
                      <td>{client.email || '-'}</td>
                      <td>{client.case_count}</td>
                      {showFinance && <td>{formatCurrency(client.total_value)}</td>}
                      {showFinance && <td>{formatCurrency(client.total_collected)}</td>}
                      {showFinance && (
                        <td style={{ color: outstanding > 0 ? 'var(--danger)' : undefined, fontWeight: outstanding > 0 ? 600 : undefined }}>
                          {formatCurrency(outstanding)}
                        </td>
                      )}
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-icon" onClick={(e) => { e.stopPropagation(); openEditModal(client); }}>Edit</button>
                          <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={(e) => { e.stopPropagation(); setDeletingClient(client); setDeleteMode(null); }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* New / Edit Client Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClient ? 'Edit Client' : 'New Client'}</h2>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18 }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input className="form-input" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">File Reference</label>
                  <input className="form-input" type="text" placeholder="e.g. SKR/2024/001" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contact</label>
                  <input className="form-input" type="text" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label">Address</label>
                <textarea className="form-textarea" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
              </div>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label">Follow-up Tasks</label>
                <textarea
                  className="form-textarea"
                  value={form.follow_up_tasks}
                  onChange={(e) => setForm({ ...form, follow_up_tasks: e.target.value })}
                  rows={3}
                  placeholder="Enter follow-up tasks, reminders, or action items..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Client Confirmation Modal */}
      {deletingClient && (
        <div className="modal-overlay" onClick={() => { setDeletingClient(null); setDeleteMode(null); }}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Client</h2>
              <button onClick={() => { setDeletingClient(null); setDeleteMode(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)' }}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16 }}>
                How would you like to handle <strong>{deletingClient.name}</strong>'s data?
              </p>

              <div
                onClick={() => setDeleteMode('archive')}
                style={{
                  border: `2px solid ${deleteMode === 'archive' ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 8,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  marginBottom: 10,
                  background: deleteMode === 'archive' ? 'rgba(196,30,58,0.04)' : '#fff',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Retain Data (Archive)</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Remove the client's personal information (name, contact, email, address) but keep all their cases, payments, and history intact in the system.
                </div>
              </div>

              <div
                onClick={() => setDeleteMode('full')}
                style={{
                  border: `2px solid ${deleteMode === 'full' ? 'var(--danger)' : 'var(--border)'}`,
                  borderRadius: 8,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  background: deleteMode === 'full' ? 'rgba(239,68,68,0.04)' : '#fff',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--danger)' }}>Delete Everything</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Permanently delete this client and ALL their cases, payments, and stage history. This cannot be undone.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => { setDeletingClient(null); setDeleteMode(null); }}>Cancel</button>
              <button
                className="btn"
                style={{ background: deleteMode === 'full' ? 'var(--danger)' : deleteMode === 'archive' ? 'var(--primary)' : 'var(--border)', color: deleteMode ? '#fff' : 'var(--text-secondary)' }}
                onClick={handleDelete}
                disabled={!deleteMode || deleting}
              >
                {deleting ? 'Processing...' : deleteMode === 'archive' ? 'Archive Client' : deleteMode === 'full' ? 'Delete Everything' : 'Select an option above'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
